import React, { useState, useEffect } from 'react';
import { useWizardFormContext } from 'providers/WizardFormProvider';
import { Form, Table, Button, FormControlProps } from 'react-bootstrap';
import { WizardFormData } from 'components/forms/CampaignWizardForm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import {
  fetchBXPCallingLists,
  fetchBXPTreatments,
  fetchBXPCampaignCallinglist
} from 'services/hvaservicecampaign.service';

interface CallingList {
  id: number;
  shortname: string;
}

interface Treatment {
  id: number;
  shortname: string;
}

interface CampaignCallingListAssociation {
  id?: number;
  campaign_id: number;
  callinglist_id: number;
  treatment_id: number;
  filterby: string;
  orderby: string;
  active: boolean;
}

interface WizardCallingListsFormProps {
  campaignId?: number;
}

const WizardCallingListsForm: React.FC<WizardCallingListsFormProps> = ({
  campaignId
}) => {
  const { formData, onChange } = useWizardFormContext<WizardFormData>();
  const [callingLists, setCallingLists] = useState<CallingList[]>([]);
  const [treatments, setTreatments] = useState<Treatment[]>([]);
  // eslint-disable-next-line
  const [associations, setAssociations] = useState<CampaignCallingListAssociation[]
  >(formData.callingListAssociations || []);
  const [newAssociation, setNewAssociation] =
    useState<CampaignCallingListAssociation>({
      campaign_id: campaignId || 0,
      callinglist_id: 0,
      treatment_id: 0,
      filterby: '',
      orderby: '',
      active: true
    });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [callingListsData, treatmentsData] = await Promise.all([
          fetchBXPCallingLists(),
          fetchBXPTreatments()
        ]);
        setCallingLists(callingListsData);
        setTreatments(treatmentsData);

        if (campaignId && !formData.callingListAssociations) {
          // eslint-disable-next-line
          const campaignCallingLists = await fetchBXPCampaignCallinglist(campaignId);
          setAssociations(campaignCallingLists);
          // eslint-disable-next-line
          onChange({ target: { name: 'callingListAssociations', value: campaignCallingLists } } as any);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();
  }, [campaignId, formData.callingListAssociations, onChange]);

  const handleAddAssociation = () => {
    // eslint-disable-next-line
    if (newAssociation.callinglist_id === 0 && newAssociation.treatment_id === 0) {
      // Si no se ha seleccionado una Calling List Y un Treatment, no hacemos nada
      return;
    }

    const updatedAssociations = [
      ...associations,
      { ...newAssociation, id: Date.now() }
    ];
    setAssociations(updatedAssociations);
    // eslint-disable-next-line
    onChange({ target: { name: 'callingListAssociations', value: updatedAssociations } } as any);
    setNewAssociation({
      ...newAssociation,
      callinglist_id: 0,
      treatment_id: 0,
      filterby: '',
      orderby: '',
      active: true
    });
  };

  const handleRemoveAssociation = (id: number) => {
    const updatedAssociations = associations.filter(a => a.id !== id);
    setAssociations(updatedAssociations);
    // eslint-disable-next-line
    onChange({ target: { name: 'callingListAssociations', value: updatedAssociations } } as any);
  };

  const handleInputChange: FormControlProps['onChange'] = e => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const { name, value, type } = target;
    setNewAssociation(prev => ({
      ...prev,
      // eslint-disable-next-line
      [name]: type === 'checkbox' ? (target as HTMLInputElement).checked
          : name === 'callinglist_id' || name === 'treatment_id'
          ? Number(value)
          : value
    }));
  };

  // eslint-disable-next-line
  const isAddButtonDisabled = newAssociation.callinglist_id === 0 || newAssociation.treatment_id === 0;

  return (
    <>
      <p className="fs-9 text-primary">
        Associate Calling Lists with this Campaign
      </p>

      <div className="border rounded p-0 bg-body">
        <Table className="phoenix-table">
          <thead className="fs-10 pt-2 pb-1 border_bottom">
            <tr>
              <th className="px-5 pb-1 fs-10 fw-bold text-body-tertiary">
                Calling List
              </th>
              <th className="px-4 pb-1 fs-10 fw-bold text-body-tertiary">
                Treatment
              </th>
              <th className="px-4 pb-1 fs-10 fw-bold text-body-tertiary">
                Filter By
              </th>
              <th className="px-4 pb-1 fs-10 fw-bold text-body-tertiary">
                Order By
              </th>
              <th className="px-1 pb-1 fs-10 fw-bold text-body-tertiary">
                Active
              </th>
              <th className="px-5 pb-1 fs-11">·</th>
            </tr>
          </thead>
          <tbody className="fs-10 pt-2 pb-1">
            {associations.map(association => (
              <tr key={association.id} className="bg-body-emphasis">
                <td className="px-5 pt-1 pb-1 fs-9">
                  {
                    callingLists.find(
                      cl => cl.id === association.callinglist_id
                    )?.shortname
                  }
                </td>
                <td className="px-4 pt-1 pb-1 fs-9">
                  {
                    treatments.find(t => t.id === association.treatment_id)
                      ?.shortname
                  }
                </td>
                <td className="px-4 pt-1 pb-1 fs-9">{association.filterby}</td>
                <td className="px-4 pt-1 pb-1 fs-9">{association.orderby}</td>
                <td className="px-1 pt-1 pb-1 fs-9">
                  {association.active ? 'Yes' : 'No'}
                </td>
                <td className="pe-2 pt-1 pb-1 fs-9">
                  <Button
                    variant="primary"
                    size="sm"
                    // eslint-disable-next-line
                    onClick={() => association.id && handleRemoveAssociation(association.id)}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </td>
              </tr>
            ))}
            <tr className="border-0">
              <td className="px-3 border-0">
                <Form.Control
                  as="select"
                  name="callinglist_id"
                  value={newAssociation.callinglist_id}
                  onChange={handleInputChange}
                >
                  <option value="">Select a calling list</option>
                  {callingLists.map(list => (
                    <option key={list.id} value={list.id}>
                      {list.shortname}
                    </option>
                  ))}
                </Form.Control>
              </td>
              <td className="border-0">
                <Form.Control
                  as="select"
                  name="treatment_id"
                  value={newAssociation.treatment_id}
                  onChange={handleInputChange}
                >
                  <option value="">Select a treatment</option>
                  {treatments.map(treatment => (
                    <option key={treatment.id} value={treatment.id}>
                      {treatment.shortname}
                    </option>
                  ))}
                </Form.Control>
              </td>
              <td className="border-0">
                <Form.Control
                  type="text"
                  name="filterby"
                  value={newAssociation.filterby}
                  onChange={handleInputChange}
                  placeholder="Filter By"
                />
              </td>
              <td className="border-0">
                <Form.Control
                  type="text"
                  name="orderby"
                  value={newAssociation.orderby}
                  onChange={handleInputChange}
                  placeholder="Order By"
                />
              </td>
              <td className="border-0 fs-9">
                <div className="form-check form-switch pb-1 pt-1">
                  <label className="form-check-label">
                    <input
                      type="checkbox"
                      name="active"
                      checked={newAssociation.active}
                      onChange={handleInputChange}
                      className="form-check-input"
                    />
                    .
                  </label>
                </div>
              </td>
              <td className="border-0">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleAddAssociation}
                  disabled={isAddButtonDisabled}
                >
                  <FontAwesomeIcon icon={faPlus} />
                </Button>
              </td>
            </tr>
          </tbody>
        </Table>
      </div>
    </>
  );
};

export default WizardCallingListsForm;
